import {stringify} from 'qs';
import {createSelector} from 'reselect';

import {getSelectedCurrencyCode} from '@stubhub/react-currency-exchange';
import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';

import {isCategoryOrDeliveryTypeChanged, sliderValueChanged} from '../../helpers/filter-change-detection-helper/index';
import {getSeatTicketTraits} from '../../helpers/get-seat-traits';
import {
  daysToEventSelector,
  genreSelector,
  inventoryMarketSelector,
  eventCountryCodeSelector,
  showRedirectionModalSelector,
} from '../../store/event/selectors';
import {
  isPayPalPayLaterEnabledSelector,
  isRedirectModalEnabledSelector,
  showImportantInfoModalSelector,
} from '../../store/gsConfig/FF/selectors';
import {currentLocaleSelector, storeIdSelector} from '../../store/gsConfig/selectors';
import {forceShowDisclaimerSelector} from '../../store/header/selectors';
import {GROUPING_IDS} from '../../utils/constants';
import {SECONDS_OF_MIN} from '../../utils/timeConsts';

import {isCurrencyEligibleForPayLater} from './containers/react-build-your-order/utils';
import {closeImportantDisclaimer, enhanceListings} from './reducers';
function makeArrayToObject(list = [], key = '') {
  return list.reduce((result, item) => {
    result[item[key]] = item;

    return result;
  }, {});
}

const THIRTY = 30;
const THIRTY_MINUTES = THIRTY * SECONDS_OF_MIN;

const EVENT_CACHE_MAX_AGE = process.env.EVENT_CACHE_MAX_AGE || THIRTY_MINUTES; // 30 minutes
const PERFORMER_CACHE_MAX_AGE = process.env.PERFORMER_CACHE_MAX_AGE || THIRTY_MINUTES; // 30 minutes
const VENUE_CACHE_MAX_AGE = process.env.VENUE_CACHE_MAX_AGE || THIRTY_MINUTES; // 30 minutes
const BFF_EVENT_AKAMAI_CACHE_MAX_AGE = 3600; // 1 hour
const PROMISE_FULFILLED = 'fulfilled';

const FETCH_SIZE = 20;
const userStateSelector = (state) => state.user;
const currentLocationSelector = createSelector(userStateSelector, (user) => (user ? user.currentLocation : {}));
const deviceSelector = (state) => state.device;
const deviceTypeSelector = createSelector(deviceSelector, (device = {}) => device && device.type);
const cartSelector = createSelector(
  (state = {}) => state.cart,
  (cart) => cart
);
const listingsSelector = createSelector(
  (state = {}) => state.listings,
  (listings = {}) => {
    const {
      hasTickets,
      totalListings,
      availableQuantities = [],
      listingLoading,
      eventPricingSummary = {},
      data = [],
      pricingSummary = {},
      eventStats,
      error,
    } = listings;
    const {averageTicketPriceWithCurrency = {}} = pricingSummary;
    const seatTraits = getSeatTicketTraits(eventStats);

    return {
      hasTickets,
      totalListings,
      availableQuantities,
      listingLoading,
      listingError: error,
      eventPricingSummary,
      data,
      averageTicketPriceWithCurrency,
      seatTraits,
      listings,
    };
  }
);
const mapboxDynamicAttributeSelector = createSelector(
  (state = {}) => state.event,
  (event) => {
    const result = null;
    if (event) {
      const {dynamicAttributes = []} = event;
      const [firstAttribute] = dynamicAttributes.filter((attribute) => attribute.name === 'map_detail_level');
      if (firstAttribute) {
        return {
          mapType: firstAttribute || {},
          mapStatus: firstAttribute.status || 'active',
          mapValue: firstAttribute.value || 'default',
        };
      }
    }

    return result;
  }
);
const eventDateLocal = ({event = {}}) => event.eventDateLocal;

const defaultToBestValueSortforWorldSeries = ({event = {}}) => {
  const {groupings = [], eventAttributes = {}} = event;
  if (groupings.length && eventAttributes.eventType !== 'Parking') {
    return event.groupings.filter((grouping = {}) => grouping.id === GROUPING_IDS.WORLD_SERIES).length;
  }
};

// Feature toggles
const featuresSelector = createSelector(
  ({gsConfig = {}}) => gsConfig.features,
  (features) => {
    if (!features) {
      return {};
    }

    return {
      showLegal: features['event.legalMessages.show'],
      isPriceIncludeFeesPreselected: features['event.legal.priceIncludeFees.select'],
      enableEventContentBFE: features['event.bfe.content'],
      enableMerchHeader: features['event.enableEventHeader'],
      disableMerchHeaderMweb: features['event.disableMwebHeader'],
      mapboxForcedEnable: features['event.seatmap.mapbox.force'],
    };
  }
);

const mapboxFeatureEnableSelector = createSelector(
  ({gsConfig = {}}) => gsConfig.features,
  mapboxDynamicAttributeSelector,
  (features, mapDetails) => {
    return (
      features &&
      features['event.seatmap.mapbox'] &&
      mapDetails &&
      mapDetails.mapStatus === 'active' &&
      ['section', 'row', 'seat'].includes(mapDetails.mapValue)
    );
  }
);

const mapInventoryLoadingSelector = createSelector(
  (state) => state.eventPageRoyalSeatmap,
  (mapState) => {
    const {listingLoading} = mapState || false;

    return {
      mapInventoryLoading: listingLoading,
    };
  }
);

function fetchEventStructureData(eventId, lang, shstoreId) {
  const searchOptions = {
    id: eventId,
    sourceId: -1,
    shstore: shstoreId,
  };
  searchOptions.fieldList = 'id,ticketInfo,name,eventDateLocal,eventInfoUrl,description,imageUrl,createdDate,eventType';

  return get({
    host: process.env.REACT_APP_API_HOST,
    path: `/search/catalog/events/v3/?${stringify(searchOptions)}`,
    headers: _getFetchRequestHeaders(lang),
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
  });
}

const NAMESPACE = 'event';
const controller = new Controller({
  namespace: NAMESPACE,
  mapStateToProps(state) {
    const eventState = this.getLocalState(state);
    const {
      listingSelected,
      rowListingSelected,
      filterChanged,
      ancestors,
      eventStructureData,
      eventDateUTC,
      dynamicAttributes,
      currencyCode,
    } = eventState;
    const isPaypalPayLaterFFEnabled = isPayPalPayLaterEnabledSelector(state);

    return {
      ...eventState,
      device: deviceSelector(state),
      deviceType: deviceTypeSelector(state),
      ...mapboxDynamicAttributeSelector(state),
      selectedCurrency: getSelectedCurrencyCode(),
      limit: FETCH_SIZE,
      cart: cartSelector(state),
      screenSize: state.viewport && state.viewport.screenSize,
      eventDateLocal: eventDateLocal(state),
      listingSelected,
      rowListingSelected,
      ...listingsSelector(state),
      defaultToBestValueSortforWorldSeries: defaultToBestValueSortforWorldSeries(state),
      ...featuresSelector(state),
      filterChanged,
      mapboxFeatureEnable: mapboxFeatureEnableSelector(state),
      ...mapInventoryLoadingSelector(state),
      currentLocation: currentLocationSelector(state),
      storeId: storeIdSelector(state),
      visitLocale: currentLocaleSelector(state),
      inventoryMarket: inventoryMarketSelector(state),
      daysToEvent: daysToEventSelector(state),
      eventCountry: eventCountryCodeSelector(state),
      genre: genreSelector(state),
      ancestors,
      eventStructureData,
      eventDateUTC,
      dynamicAttributes,
      showImportantInfoModal: showImportantInfoModalSelector(state) || false,
      forceShowDisclaimer: forceShowDisclaimerSelector(state),
      isPaypalPayLaterEnabled: isPaypalPayLaterFFEnabled && isCurrencyEligibleForPayLater(currencyCode),
      showRedirectionModal: showRedirectionModalSelector(state),
      isRedirectionModalEnabled: isRedirectModalEnabledSelector(state),
    };
  },
  actionCreators: {
    fetchEvent,
    setListingSelected,
    destroy,
    closeDisclaimer,
    forceOpenDisclaimer,
    filterChangeDetection,
    updateDefaultCurrencies,
  },
  reducers: [
    'EVENT:LISTING_SELECTED',
    'EVENT:LOADING',
    {action: 'EVENT:LOADED', handler: enhanceListings},
    'EVENT:LISTING:SELECTED',
    'EVENT:LISTING:SELECTED:ERROR',
    {action: 'EVENT:DISCLAIMER:CLOSE', handler: closeImportantDisclaimer},
    'EVENT:FILTERCHANGED',
    'EVENT:FILTERNOTCHANGED',
  ],
});

function forceOpenDisclaimer() {
  return (dispatch) => {
    dispatch({
      type: 'EVENT:DISCLAIMER:OPEN',
      forceShowDisclaimer: true,
    });
  };
}

function closeDisclaimer() {
  return (dispatch) => {
    dispatch({
      type: 'EVENT:DISCLAIMER:CLOSE',
      showDisclaimer: false,
    });
  };
}

function filterChangeDetection(currentProps, nextProps) {
  return (dispatch) => {
    if (currentProps && nextProps) {
      const {
        priceWithFees: currentPriceWithFees,
        listingCategories: currentListingCategories,
        excludeCategories: currentExcludeCategories,
        deliveryType: currentDeliveryType,
        sliderMin: currentSliderMin,
        sliderMax: currentSliderMax,
        quantity: currentQuantity,
      } = currentProps;
      const {priceWithFees, listingCategories, excludeCategories, deliveryType, sliderMin, sliderMax, quantity} =
        nextProps;

      if (
        priceWithFees !== currentPriceWithFees ||
        isCategoryOrDeliveryTypeChanged(listingCategories, currentListingCategories) ||
        isCategoryOrDeliveryTypeChanged(excludeCategories, currentExcludeCategories) ||
        isCategoryOrDeliveryTypeChanged(deliveryType, currentDeliveryType) ||
        sliderValueChanged(sliderMin, currentSliderMin) ||
        sliderValueChanged(sliderMax, currentSliderMax) ||
        quantity !== currentQuantity
      ) {
        dispatch({
          type: 'EVENT:FILTERCHANGED',
          filterChanged: true,
        });
      } else {
        dispatch({
          type: 'EVENT:FILTERNOTCHANGED',
          filterChanged: false,
        });
      }
    } else {
      dispatch({
        type: 'EVENT:FILTERNOTCHANGED',
        filterChanged: false,
      });
    }
  };
}

function setListingSelected(listingId) {
  return (dispatch, getState) => {
    const {listings} = getState();
    if (listings) {
      const [listing] = listings.data.filter(({seats}) => Array.isArray(seats) && seats[0].listingId === listingId);
      let rowListings = null;
      let sectionListings = null;

      if (listing) {
        const {sectionId, seats} = listing;
        const [{row}] = seats;
        sectionListings = listings.data.filter((oneListing) => {
          return sectionId === oneListing.sectionId;
        });
        rowListings = sectionListings.filter((oneListing) => {
          if (Array.isArray(oneListing.seats)) {
            const oneListingSeats = oneListing.seats;
            const oneRow = oneListingSeats[0].row;

            return row === oneRow;
          }

          return false;
        });
      }

      dispatch({type: 'EVENT:LISTING:SELECTED', listingSelected: listing, rowListingSelected: rowListings});
    } else if (listingId === null) {
      dispatch({type: 'EVENT:LISTING:SELECTED', listingSelected: null, rowListingSelected: null});
    } else {
      dispatch({type: 'EVENT:LISTING:SELECTED:ERROR', listingSelected: null, rowListingSelected: null});
    }
  };
}

// Controller.addResetReducer(['EVENT:RESET']);

function destroy() {
  return function (dispatch) {
    dispatch({type: 'EVENT:RESET'});
    dispatch({type: 'EVENT:LISTING_RESET'});
  };
}

function getEventDetailsUrl(eventId) {
  return `/exp-buy/event/${eventId}/content/v1/`;
}

function updateDefaultCurrencies() {
  return (dispatch, getState) => {
    const state = getState();
    const localState = state[NAMESPACE] || {};
    const {currencyCode} = localState;
    dispatch({type: 'DEFAULT_CURRENCIES_UPDATE', defaultCurrencyCodes: [currencyCode]});
  };
}

function fetchEvent(eventId) {
  return async (dispatch, getState, {logger}) => {
    const state = getState();
    const localState = state[NAMESPACE] || {};
    const {loaded: eventHasLoaded, loading: eventIsLoading} = localState;

    if (!eventHasLoaded && !eventIsLoading) {
      dispatch({type: 'EVENT:LOADING', loading: true, loaded: false, eventId});
      const {gsConfig, lang} = state;
      const {shstoreId, features} = gsConfig;

      const eventDetailsData = await getEventInfo(eventId, lang, shstoreId, features, logger);
      const {venueConfigurations: venueConfig, paymentMethods, eventFeaturesByCountry} = eventDetailsData || {};

      const {event = {}, eventStructureData, performersDetails} = eventDetailsData;

      if (event.expiredInd) {
        dispatch({
          type: 'EVENT:LOADED',
          loading: false,
          loaded: true,
          isEventExpired: true,
          ...event,
          eventStructureData,
          allPerformers: event.performers,
        });
      } else if (!venueConfig) {
        /*
         * TODO: this is a temporary hack to send the user Back to where they came from instead of redirecting to home
         *  when venue config data is unavailable
         */
        logger && logger.error(`Event-venueConfigFailure no venueConfig found event: ${eventId}`);

        /*
         * Temporarily disabled SITEOPS-12683
         * history && history.goBack();
         */
      } else {
        const [venueConfigResult] = (venueConfig && venueConfig.venueConfiguration) || [];
        const {seatTraits = [], eventAttributes} = event || {};
        const {eventType = 'Main'} = eventAttributes;
        const loadedPerformers = performersDetails;
        const firstPerformer = (loadedPerformers && loadedPerformers.length && loadedPerformers[0]) || {};
        const {images: performerImages = []} = firstPerformer;
        const eventImages = performerImages.length ? performerImages : event.images;
        const reco = venueConfigResult && venueConfigResult.attributes && venueConfigResult.attributes.reco;
        const showTicketRecoInd = (reco && reco.showTicketRecoInd) || false;
        const {buyerMessages = []} = event;
        const hasBuyerMessages = buyerMessages && buyerMessages.length > 0;

        /**
         * If we don't have a event.sectionZoneToggle.show set, then show HybridMap
         * @type {boolean}
         */
        const showHybridMap = !eventFeaturesByCountry['event.sectionZoneToggle.show'];
        const showFaceValue = eventFeaturesByCountry['event.faceValue.show'];

        dispatch({type: 'DEFAULT_CURRENCIES_UPDATE', defaultCurrencyCodes: [event.currencyCode]});
        dispatch({
          type: 'EVENT:LOADED',
          loading: false,
          loaded: true,
          ...event,
          isGA: venueConfigResult && venueConfigResult.generalAdmissionOnly,
          venueConfig: venueConfigResult,
          showTicketRecoInd,
          showHybridMap,
          performers: loadedPerformers,
          firstPerformer,
          eventImages,
          performerType: firstPerformer && firstPerformer.performerType,
          firstPerformerId: firstPerformer && firstPerformer.id,
          seatTraits,
          showFaceValue,
          paymentMethods,
          showDisclaimer: hasBuyerMessages,
          eventType,
          eventStructureData,
          allPerformers: event.performers,
        });
      }
    }
  };
}

async function getEventInfo(eventId, lang, shstoreId, features, logger) {
  if (process.env.REACT_APP_BFE_ENABLED === 1 || features['event.bfe.content'] === true) {
    return await getEventDataBFE(eventId, lang, shstoreId, features);
  }

  return await getEventData(eventId, lang, shstoreId, features, logger);
}

async function getEventDataBFE(eventId, lang, shstoreId, features) {
  try {
    const contentResponse = await get({
      host: process.env.REACT_APP_API_HOST,
      path: getEventDetailsUrl(eventId, shstoreId),
      headers: {
        'Accept-Language': lang,
        'Cache-Control': `public, max-age=${BFF_EVENT_AKAMAI_CACHE_MAX_AGE}`,
      },
      json: true,
      cache: true,
      cacheMaxAge: EVENT_CACHE_MAX_AGE,
    });
    const {event: eventResponse, performersDetails, venueConfigurations} = contentResponse;
    const {
      venue: {country},
    } = eventResponse;

    const allPromises = [
      getFeaturesByEventCountry(country, lang),
      getPaymentReceivables(eventResponse.currencyCode, shstoreId, features, lang),
      fetchEventStructureData(eventId, lang, shstoreId),
    ];

    return Promise.all(allPromises)
      .then(([eventFeaturesByCountry, paymentReceivablesResponse, eventStructureDataResponse]) => {
        const response = {
          event: eventResponse,
          eventFeaturesByCountry: eventFeaturesByCountry.features
            ? makeArrayToObject(eventFeaturesByCountry.features, 'name')
            : {},
          venueConfigurations,
          performersDetails,
          eventStructureData:
            (eventStructureDataResponse && eventStructureDataResponse.events && eventStructureDataResponse.events[0]) ||
            {},
        };

        if (paymentReceivablesResponse) {
          response.paymentMethods = paymentReceivablesResponse;
        }

        return response;
      })
      .catch((error) => {
        return error;
      });
  } catch (error) {
    return error;
  }
}

/** We need to remove following block once BFF layer becomes stable */

async function getEventData(eventId, lang, shstoreId, features, logger) {
  try {
    const promises = [getEvent(eventId, lang, shstoreId), fetchEventStructureData(eventId, lang, shstoreId)];
    const [eventResponse, eventStructureDataResponse] = await Promise.all(promises);
    const eventStructureData = eventStructureDataResponse?.events?.[0] || {};

    const {venue: {country, configurationId: venueConfigId} = {}, performers, currencyCode} = eventResponse;
    const performerOneId = performers?.[0]?.id;
    const performerTwoId = performers?.[1]?.id;

    const allPromises = [
      getVenueConfiguration(venueConfigId, eventId, lang, shstoreId),
      getFeaturesByEventCountry(country, lang),
      getPerformerDetail(shstoreId, lang, performerOneId),
      getPerformerDetail(shstoreId, lang, performerTwoId),
      getPaymentReceivables(currencyCode, shstoreId, features, lang),
    ];

    return Promise.allSettled(allPromises)
      .then(
        ([
          getVenueConfigurationsResponse,
          eventFeaturesByCountryResponse,
          performerOneResponse,
          performerTwoResponse,
          paymentReceivablesResponse,
        ]) => {
          const {status: getVenueConfigurationsStatus, value: venueConfigurations} = getVenueConfigurationsResponse;
          const {status: eventFeaturesByCountryStatus, value: eventFeaturesByCountry} = eventFeaturesByCountryResponse;
          const {status: paymentReceivableStatus, value: paymentReceivables} = paymentReceivablesResponse;
          const eventFeaturesByCountryMap =
            eventFeaturesByCountryStatus === PROMISE_FULFILLED && eventFeaturesByCountry.features
              ? makeArrayToObject(eventFeaturesByCountry.features, 'name')
              : {};
          const {status: performerOneStatus, value: performerOne} = performerOneResponse;
          const {status: performerTwoStatus, value: performerTwo} = performerTwoResponse;
          const performerDetails = [];
          if (performerOneStatus === PROMISE_FULFILLED) {
            performerDetails.push({...performerOne, role: performers?.[0]?.role});
          }

          if (performerTwoStatus === PROMISE_FULFILLED) {
            performerDetails.push({...performerTwo, role: performers?.[1]?.role});
          }

          const response = {
            event: eventResponse,
            eventStructureData,
            eventFeaturesByCountry: eventFeaturesByCountryMap,
            venueConfigurations: getVenueConfigurationsStatus === PROMISE_FULFILLED ? venueConfigurations : [],
            paymentMethods: paymentReceivableStatus === PROMISE_FULFILLED ? paymentReceivables : [],
            performerDetails,
          };

          return response;
        }
      )
      .catch((error) => {
        logger?.error(error);

        return error;
      });
  } catch (error) {
    logger?.error(error);

    return error;
  }
}

function getEvent(eventId, lang, shstore = 1) {
  return get({
    host: process.env.REACT_APP_API_HOST,
    path: `/catalog/events/v3/${eventId}/?mode=internal&isSeatTraitsRequired=true&shstore=${shstore}`,
    headers: _getFetchRequestHeaders(lang),
    json: true,
    cache: true,
    cacheMaxAge: EVENT_CACHE_MAX_AGE,
  });
}

function getVenueConfiguration(venueConfigId, eventId, lang, shstoreId) {
  const venueConfigUrl = `/catalog/venues/v3/venueconfigurations?includeMetas=true&venueConfigId=${venueConfigId}&eventId=${eventId}&isSectionZoneRequired=true&shstore=${shstoreId}`;

  return get({
    host: process.env.REACT_APP_API_HOST,
    path: venueConfigUrl,
    headers: _getFetchRequestHeaders(lang),
    json: true,
    cache: true,
    cacheMaxAge: VENUE_CACHE_MAX_AGE,
  });
}

function getPaymentReceivables(eventCurrency, shstore = 1, features, lang) {
  if (features['common.enableTrustMessaging']) {
    const query = {
      currency: eventCurrency,
      shstore,
      gcp: true,
    };
    const endpoint = '/payments/instruments/receivabletypes/v1';

    return get({
      host: process.env.REACT_APP_GCP_CLOUD_API_HOST,
      path: endpoint,
      headers: _getFetchRequestHeaders(lang),
      json: true,
      query,
      cache: true,
      cacheMaxAge: EVENT_CACHE_MAX_AGE,
    });
  }

  return Promise.resolve();
}

function getPerformerDetail(shstoreId, lang, performerId) {
  if (performerId) {
    return get({
      host: process.env.REACT_APP_API_HOST,
      path: getCatalogPerformersUrl(performerId, shstoreId),
      headers: {
        'Accept-Language': lang,
        Authorization: process.env.REACT_APP_API_SECRET,
      },
      json: true,
      cache: true,
      cacheMaxAge: PERFORMER_CACHE_MAX_AGE,
    });
  }

  return Promise.resolve();
}

/**
 * Get the features for an event venue's country by calling the
 * corresponding API or pull from cache. Cache the results.
 * Returns a promise
 * '/shape/i18n/globalregistry/v2/countries/' + countryCode + '/features'
 * @param  {String} countryCode - Two digit country code (e.g. 'US', 'GB', etc)
 * @param  {String} lang
 * @return {Promise}
 */
function getFeaturesByEventCountry(countryCode, lang) {
  const eventFeaturesUrl = `/i18n/globalregistry/v2/countries/${countryCode}/features`;

  return get({
    host: process.env.REACT_APP_API_HOST,
    path: eventFeaturesUrl,
    headers: _getFetchRequestHeaders(lang),
    json: true,
    cache: true,
    cacheMaxAge: VENUE_CACHE_MAX_AGE,
  });
}

/*
 *@param {string} performerId
 *@param {number} shstoreId {default value 1 for US domain}
 *@return {string} // api catalog performer Url
 */

function getCatalogPerformersUrl(performerId, shstoreId = 1) {
  const opts = {
    mode: 'internal',
    shstore: shstoreId,
  };

  return `/catalog/performers/v3/${performerId}?${stringify(opts)}`;
}

function _getFetchRequestHeaders(language) {
  const ret = {
    Authorization: process.env.REACT_APP_API_SECRET,
  };
  if (language) {
    ret['Accept-Language'] = language;
  }

  return ret;
}

export default controller;
